import React, { FormEvent, useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import toast, { Toaster } from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { TradeshowOrderService } from '../../services/TradeshowOrderService';
import { DisplayPanelCount, TradeshowOrderFormData } from '../../@types/tradeshowOrder';
import {
  EMAIL_ADDRESSES,
  TRADESHOW_ACCOUNT_MANAGER_NAME,
} from '../../config/emailAddresses';
import { getRecaptchaToken, isRecaptchaEnabled } from '../../utils/recaptcha';
import { usePlacesAddressAutocomplete } from '../../hooks/usePlacesAddressAutocomplete';

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Oswald:wght@500;600;700&family=Source+Sans+3:wght@400;600;700&display=swap';

const PAGE = {
  ink: '#1c2118',
  forest: '#1f5c2e',
  leaf: '#2f7a3c',
  headline: '#4a6274',
  paper: '#fffcf6',
  floor: '#14301c',
  line: '#1f5c2e',
  muted: '#5d6a58',
};

const Page = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(circle at 12% 8%, rgba(148, 204, 0, 0.12), transparent 32%),
    radial-gradient(circle at 88% 92%, rgba(31, 92, 46, 0.45), transparent 40%),
    linear-gradient(165deg, #1a3a22 0%, ${PAGE.floor} 48%, #0e1f13 100%);
  padding: 28px 16px 48px;
  display: flex;
  justify-content: center;
  overflow-x: hidden;

  .grecaptcha-badge {
    visibility: hidden;
  }
`;

const Sheet = styled.main`
  width: 100%;
  max-width: 760px;
  background: ${PAGE.paper};
  color: ${PAGE.ink};
  padding: 36px 42px 40px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.35) inset,
    0 24px 60px rgba(0, 0, 0, 0.35);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 10px;
    border: 1px solid rgba(31, 92, 46, 0.12);
    pointer-events: none;
  }

  @media (max-width: 640px) {
    padding: 24px 16px 28px;

    &::before {
      inset: 6px;
    }
  }
`;

const Brand = styled.header`
  text-align: center;
  margin: 28px 0 18px;
`;

const LangSwitch = styled.div`
  position: absolute;
  top: 16px;
  right: 18px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0;
  font-family: 'Oswald', sans-serif;
  letter-spacing: 0.08em;

  @media (max-width: 640px) {
    top: 10px;
    right: 10px;
  }
`;

const LangButton = styled.button<{ $active: boolean }>`
  appearance: none;
  background: ${(p) => (p.$active ? PAGE.forest : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : PAGE.forest)};
  border: 1.5px solid ${PAGE.forest};
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  padding: 5px 10px;
  cursor: pointer;
  min-width: 44px;

  &:first-child {
    border-right: none;
  }

  &:hover {
    background: ${(p) => (p.$active ? PAGE.leaf : 'rgba(31, 92, 46, 0.08)')};
  }

  &:focus-visible {
    outline: 3px solid ${PAGE.headline};
    outline-offset: 2px;
  }
`;

const Logo = styled.img`
  width: min(320px, 86vw);
  height: auto;
  display: block;
  margin: 0 auto 6px;
`;

const Headline = styled.h1`
  font-family: 'Oswald', 'Arial Narrow', sans-serif;
  font-weight: 700;
  font-size: clamp(1.35rem, 5.4vw, 3.15rem);
  letter-spacing: 0.03em;
  text-align: center;
  margin: 10px 0 16px;
  text-transform: uppercase;
  color: ${PAGE.headline};
  -webkit-text-stroke: 1px #31485a;
  paint-order: stroke fill;
  line-height: 1.12;
  padding: 0 4px;

  @media (min-width: 640px) {
    letter-spacing: 0.04em;
    -webkit-text-stroke: 1.4px #31485a;
  }
`;

const Benefits = styled.div`
  text-align: center;
  margin-bottom: 28px;
`;

const Benefit = styled.p`
  font-family: 'Oswald', sans-serif;
  font-weight: 600;
  font-size: clamp(1.05rem, 3.2vw, 1.45rem);
  color: ${PAGE.leaf};
  letter-spacing: 0.02em;
  margin: 0 0 4px;
`;

const DisplaySectionTitle = styled.h2`
  font-family: 'Oswald', sans-serif;
  font-size: 1.15rem;
  font-weight: 600;
  color: ${PAGE.forest};
  letter-spacing: 0.04em;
  margin: 8px 0 4px;
`;

const DisplayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin: 12px 0 16px;

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const DisplayItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DisplayCard = styled.button<{ $selected: boolean }>`
  appearance: none;
  background: #fff;
  border: 2px solid ${(p) => (p.$selected ? PAGE.forest : 'transparent')};
  box-shadow: ${(p) =>
    p.$selected ? `0 0 0 3px rgba(31, 92, 46, 0.18)` : '0 0 0 1px rgba(28, 33, 24, 0.12)'};
  padding: 0 0 10px;
  cursor: pointer;
  text-align: center;
  overflow: hidden;
  transition: box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
  font-family: 'Source Sans 3', sans-serif;

  &:hover {
    transform: translateY(-2px);
    border-color: ${PAGE.leaf};
  }

  &:focus-visible {
    outline: 3px solid ${PAGE.forest};
    outline-offset: 2px;
  }
`;

const DisplayPhoto = styled.img`
  width: 100%;
  height: 240px;
  object-fit: contain;
  display: block;
  background: #0b0b0b;

  @media (max-width: 640px) {
    height: 220px;
  }
`;

const DisplayLabel = styled.span`
  display: block;
  margin-top: 6px;
  font-family: 'Oswald', sans-serif;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${PAGE.ink};
  text-transform: lowercase;
`;

const SelectedMark = styled.span`
  display: inline-block;
  margin-left: 6px;
  color: ${PAGE.forest};
  font-size: 0.85rem;
`;

const QtyField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const QtyLabel = styled.label`
  font-family: 'Source Sans 3', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${PAGE.ink};
`;

const QtyInput = styled.input`
  width: 100%;
  border: none;
  border-bottom: 1.5px solid ${PAGE.line};
  background: transparent;
  font-family: 'Source Sans 3', sans-serif;
  font-size: 1.05rem;
  padding: 4px 2px 6px;
  color: ${PAGE.ink};
  border-radius: 0;

  &:focus {
    outline: none;
    border-bottom-width: 2.5px;
    background: rgba(31, 92, 46, 0.04);
  }
`;

const Form = styled.form`
  position: relative;
  z-index: 1;
`;

const Field = styled.div`
  margin-bottom: 18px;
`;

const FieldHint = styled.p`
  margin: 4px 0 0;
  font-family: 'Source Sans 3', sans-serif;
  font-size: 0.8rem;
  color: ${PAGE.muted};
`;

const PlacesDropdownStyles = createGlobalStyle`
  .pac-container {
    z-index: 4000;
    margin-top: 4px;
    border: 1px solid ${PAGE.line};
    border-radius: 0;
    box-shadow: 0 10px 28px rgba(20, 48, 28, 0.16);
    font-family: 'Source Sans 3', sans-serif;
    background: #fffcf6;
  }

  .pac-item {
    padding: 8px 10px;
    cursor: pointer;
    line-height: 1.35;
    border-top: 1px solid rgba(31, 92, 46, 0.12);
  }

  .pac-item:first-child {
    border-top: none;
  }

  .pac-item:hover,
  .pac-item-selected {
    background: rgba(31, 92, 46, 0.08);
  }

  .pac-item-query,
  .pac-matched {
    color: ${PAGE.ink};
    font-weight: 600;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 0.8fr;
  gap: 20px;
  margin-bottom: 18px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }
`;

const Label = styled.label`
  display: block;
  font-family: 'Source Sans 3', sans-serif;
  font-size: 0.98rem;
  font-weight: 600;
  color: ${PAGE.ink};
  margin-bottom: 2px;
`;

const UnderlineInput = styled.input`
  width: 100%;
  border: none;
  border-bottom: 1.5px solid ${PAGE.line};
  background: transparent;
  font-family: 'Source Sans 3', sans-serif;
  font-size: 1.05rem;
  padding: 6px 2px 8px;
  color: ${PAGE.ink};
  border-radius: 0;

  &:focus {
    outline: none;
    border-bottom-width: 2.5px;
    background: rgba(31, 92, 46, 0.04);
  }

  &:user-invalid {
    border-bottom-color: #a33;
  }
`;

const UnderlineTextarea = styled.textarea`
  width: 100%;
  min-height: 88px;
  border: none;
  border-bottom: 1.5px solid ${PAGE.line};
  background: transparent;
  font-family: 'Source Sans 3', sans-serif;
  font-size: 1.05rem;
  padding: 6px 2px 8px;
  color: ${PAGE.ink};
  border-radius: 0;
  resize: vertical;
  line-height: 1.4;

  &:focus {
    outline: none;
    border-bottom-width: 2.5px;
    background: rgba(31, 92, 46, 0.04);
  }
`;

const Honeypot = styled.div`
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const AccountBlock = styled.div`
  margin: 28px 0 8px;
  font-family: 'Source Sans 3', sans-serif;
  font-size: 1rem;
  color: ${PAGE.ink};

  strong {
    font-weight: 700;
  }
`;

const AutoNote = styled.p`
  margin: 6px 0 0;
  color: ${PAGE.muted};
  font-size: 0.92rem;
  overflow-wrap: anywhere;
`;

const SubmitButton = styled.button`
  display: block;
  width: 100%;
  margin-top: 28px;
  background: ${PAGE.forest};
  color: #fff;
  border: none;
  font-family: 'Oswald', sans-serif;
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 14px 18px;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover:not(:disabled) {
    background: ${PAGE.leaf};
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 3px solid ${PAGE.headline};
    outline-offset: 3px;
  }
`;

const ErrorText = styled.p`
  color: #9b1c1c;
  font-family: 'Source Sans 3', sans-serif;
  font-size: 0.9rem;
  margin: 8px 0 0;
`;

const RecaptchaNotice = styled.p`
  margin: 12px 0 0;
  font-family: 'Source Sans 3', sans-serif;
  font-size: 0.75rem;
  color: ${PAGE.muted};
  line-height: 1.4;

  a {
    color: ${PAGE.forest};
  }
`;

const Slogan = styled.footer`
  text-align: center;
  margin-top: 36px;
  font-family: 'Oswald', sans-serif;
  font-weight: 700;
  font-size: clamp(1.35rem, 4.2vw, 1.9rem);
  color: ${PAGE.leaf};
  line-height: 1.2;
  letter-spacing: 0.01em;
`;

const SuccessCard = styled.div`
  text-align: center;
  padding: 28px 8px 8px;
  font-family: 'Source Sans 3', sans-serif;
`;

const SuccessTitle = styled.h2`
  font-family: 'Oswald', sans-serif;
  font-size: 2rem;
  color: ${PAGE.forest};
  margin: 0 0 10px;
`;

const SuccessBody = styled.p`
  font-size: 1.05rem;
  color: ${PAGE.ink};
  margin: 0 0 22px;
  line-height: 1.5;
`;

const EMPTY_FORM: TradeshowOrderFormData = {
  storeName: '',
  storeNumber: '',
  storeAddress: '',
  contactName: '',
  email: '',
  poNumber: '',
  deliveryDate: '',
  accountManagerNotes: '',
  displays: [],
};

const DISPLAY_OPTIONS: { value: DisplayPanelCount; image: string }[] = [
  { value: '1', image: '/assets/tradeshow/1-panel.png' },
  { value: '2', image: '/assets/tradeshow/2-panels.png' },
  { value: '3', image: '/assets/tradeshow/3-panels.png' },
  { value: '4', image: '/assets/tradeshow/4-panels.png' },
];

export const TradeshowOrderScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isFrench = (i18n.resolvedLanguage || i18n.language || 'en').startsWith('fr');
  const [form, setForm] = useState<TradeshowOrderFormData>(EMPTY_FORM);
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [displayErrorKey, setDisplayErrorKey] = useState('');
  const [formOpenedAt, setFormOpenedAt] = useState(() => new Date().toISOString());
  const addressInputRef = useRef<HTMLInputElement>(null);
  const addressAutocompleteEnabled = usePlacesAddressAutocomplete(
    addressInputRef,
    (address) => setForm((prev) => ({ ...prev, storeAddress: address }))
  );

  const panelLabel = (panels: DisplayPanelCount) => t(`tradeshow.panel_${panels}`);

  const displaySummary = form.displays
    .map((item) => `${item.quantity} × ${panelLabel(item.panels)}`)
    .join(', ');

  const setLanguage = (lang: 'en' | 'fr') => {
    i18n.changeLanguage(lang);
    const url = new URL(window.location.href);
    url.searchParams.set('lang', lang);
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  };

  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get('lang');
    if (param === 'fr' || param === 'en') {
      i18n.changeLanguage(param);
    }
  }, [i18n]);

  useEffect(() => {
    document.title = t('tradeshow.pageTitle');
    document.documentElement.lang = isFrench ? 'fr' : 'en';
    return () => {
      document.title = 'Nexis ERP';
      document.documentElement.lang = 'en';
    };
  }, [t, isFrench]);

  useEffect(() => {
    if (!document.getElementById('tradeshow-fonts')) {
      const link = document.createElement('link');
      link.id = 'tradeshow-fonts';
      link.rel = 'stylesheet';
      link.href = FONT_HREF;
      document.head.appendChild(link);
    }
    if (isRecaptchaEnabled()) {
      getRecaptchaToken().catch(() => {
        /* Preload only — submit will retry. */
      });
    }
  }, []);

  const update = (field: keyof Omit<TradeshowOrderFormData, 'displays'>) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const isDisplaySelected = (panels: DisplayPanelCount) =>
    form.displays.some((item) => item.panels === panels);

  const getDisplayQuantity = (panels: DisplayPanelCount) =>
    form.displays.find((item) => item.panels === panels)?.quantity ?? 1;

  const toggleDisplay = (panels: DisplayPanelCount) => {
    setForm((prev) => {
      const selected = prev.displays.some((item) => item.panels === panels);
      return {
        ...prev,
        displays: selected
          ? prev.displays.filter((item) => item.panels !== panels)
          : [...prev.displays, { panels, quantity: 1 }].sort((a, b) => Number(a.panels) - Number(b.panels)),
      };
    });
    setDisplayErrorKey('');
  };

  const setDisplayQuantity = (panels: DisplayPanelCount, raw: string) => {
    const quantity = Math.min(99, Math.max(1, Number(raw) || 1));
    setForm((prev) => ({
      ...prev,
      displays: prev.displays.map((item) =>
        item.panels === panels ? { ...item, quantity } : item
      ),
    }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (honeypot) {
      return;
    }
    if (form.displays.length === 0) {
      setDisplayErrorKey('tradeshow.chooseDisplay');
      return;
    }
    if (form.displays.some((item) => !item.quantity || item.quantity < 1)) {
      setDisplayErrorKey('tradeshow.enterQuantity');
      return;
    }
    setDisplayErrorKey('');
    setSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken();
      const result = await TradeshowOrderService.submitOrder(form, {
        companyWebsite: honeypot,
        formOpenedAt,
        recaptchaToken,
        language: i18n.resolvedLanguage || i18n.language,
      });
      setSubmittedId(result.orderId);
      if (result.emailSent) {
        toast.success(t('tradeshow.toastEmailed'));
      } else {
        toast.success(t('tradeshow.toastSaved'));
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('tradeshow.submitFailed');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <PlacesDropdownStyles />
      <Toaster position="top-center" />
      <Sheet>
        <LangSwitch role="group" aria-label={t('tradeshow.language')}>
          <LangButton
            type="button"
            $active={!isFrench}
            onClick={() => setLanguage('en')}
            aria-pressed={!isFrench}
          >
            EN
          </LangButton>
          <LangButton
            type="button"
            $active={isFrench}
            onClick={() => setLanguage('fr')}
            aria-pressed={isFrench}
          >
            FR
          </LangButton>
        </LangSwitch>
        <Brand>
          <Logo src="/assets/erp-logo.svg" alt={t('tradeshow.logoAlt')} />
        </Brand>

        <Headline>{t('tradeshow.headline')}</Headline>
        <Benefits>
          <Benefit>{t('tradeshow.benefitCredit')}</Benefit>
          <Benefit>{t('tradeshow.benefitPayment')}</Benefit>
          <Benefit>{t('tradeshow.benefitDelivery')}</Benefit>
          <Benefit>{t('tradeshow.benefitTeam')}</Benefit>
        </Benefits>

        {submittedId ? (
          <SuccessCard>
            <SuccessTitle>{t('tradeshow.successTitle')}</SuccessTitle>
            <SuccessBody>
              {t('tradeshow.successBody', {
                summary: displaySummary ? ` (${displaySummary})` : '',
                name: TRADESHOW_ACCOUNT_MANAGER_NAME,
              })}
            </SuccessBody>
            <SubmitButton
              type="button"
              onClick={() => {
                setForm(EMPTY_FORM);
                setSubmittedId(null);
                setHoneypot('');
                setFormOpenedAt(new Date().toISOString());
              }}
            >
              {t('tradeshow.submitAnother')}
            </SubmitButton>
          </SuccessCard>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Honeypot>
              <label htmlFor="company_website">Company website</label>
              <input
                id="company_website"
                name="company_website"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(event) => setHoneypot(event.target.value)}
              />
            </Honeypot>

            <Row>
              <Field>
                <Label htmlFor="storeName">{t('tradeshow.storeName')}</Label>
                <UnderlineInput
                  id="storeName"
                  name="storeName"
                  value={form.storeName}
                  onChange={update('storeName')}
                  required
                  maxLength={200}
                  autoComplete="organization"
                />
              </Field>
              <Field>
                <Label htmlFor="storeNumber">{t('tradeshow.storeNumber')}</Label>
                <UnderlineInput
                  id="storeNumber"
                  name="storeNumber"
                  value={form.storeNumber}
                  onChange={update('storeNumber')}
                  required
                  maxLength={50}
                />
              </Field>
            </Row>

            <Field>
              <Label htmlFor="storeAddress">{t('tradeshow.storeAddress')}</Label>
              <UnderlineInput
                id="storeAddress"
                name="storeAddress"
                ref={addressInputRef}
                value={form.storeAddress}
                onChange={update('storeAddress')}
                required
                maxLength={300}
                autoComplete={addressAutocompleteEnabled ? 'off' : 'street-address'}
              />
              {addressAutocompleteEnabled && (
                <FieldHint>{t('tradeshow.addressHint')}</FieldHint>
              )}
            </Field>

            <Row>
              <Field>
                <Label htmlFor="contactName">{t('tradeshow.contactName')}</Label>
                <UnderlineInput
                  id="contactName"
                  name="contactName"
                  value={form.contactName}
                  onChange={update('contactName')}
                  required
                  maxLength={150}
                  autoComplete="name"
                />
              </Field>
              <Field>
                <Label htmlFor="email">{t('tradeshow.email')}</Label>
                <UnderlineInput
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={update('email')}
                  required
                  maxLength={150}
                  autoComplete="email"
                />
              </Field>
            </Row>

            <Row>
              <Field>
                <Label htmlFor="poNumber">{t('tradeshow.poNumber')}</Label>
                <UnderlineInput
                  id="poNumber"
                  name="poNumber"
                  value={form.poNumber}
                  onChange={update('poNumber')}
                  required
                  maxLength={80}
                />
              </Field>
              <Field>
                <Label htmlFor="deliveryDate">{t('tradeshow.deliveryDate')}</Label>
                <UnderlineInput
                  id="deliveryDate"
                  name="deliveryDate"
                  type="date"
                  value={form.deliveryDate}
                  onChange={update('deliveryDate')}
                  required
                />
              </Field>
            </Row>

            <DisplaySectionTitle>{t('tradeshow.displaySelection')}</DisplaySectionTitle>
            <FieldHint>{t('tradeshow.displayHint')}</FieldHint>
            <DisplayGrid role="group" aria-label={t('tradeshow.displaySelection')}>
              {DISPLAY_OPTIONS.map((option) => {
                const selected = isDisplaySelected(option.value);
                const label = panelLabel(option.value);
                return (
                  <DisplayItem key={option.value}>
                    <DisplayCard
                      type="button"
                      role="checkbox"
                      aria-checked={selected}
                      $selected={selected}
                      onClick={() => toggleDisplay(option.value)}
                    >
                      <DisplayPhoto src={option.image} alt={label} />
                      <DisplayLabel>
                        {label}
                        {selected && <SelectedMark aria-hidden="true">✓</SelectedMark>}
                      </DisplayLabel>
                    </DisplayCard>
                    {selected && (
                      <QtyField>
                        <QtyLabel htmlFor={`qty-${option.value}`}>{t('tradeshow.howMany')}</QtyLabel>
                        <QtyInput
                          id={`qty-${option.value}`}
                          type="number"
                          min={1}
                          max={99}
                          step={1}
                          inputMode="numeric"
                          value={getDisplayQuantity(option.value)}
                          onChange={(event) => setDisplayQuantity(option.value, event.target.value)}
                          required
                        />
                      </QtyField>
                    )}
                  </DisplayItem>
                );
              })}
            </DisplayGrid>
            {displayErrorKey && <ErrorText>{t(displayErrorKey)}</ErrorText>}

            <Field>
              <Label htmlFor="accountManagerNotes">{t('tradeshow.notes')}</Label>
              <UnderlineTextarea
                id="accountManagerNotes"
                name="accountManagerNotes"
                value={form.accountManagerNotes}
                onChange={update('accountManagerNotes')}
                maxLength={1500}
                rows={4}
              />
            </Field>

            <AccountBlock>
              <div>
                <strong>{t('tradeshow.accountManager')}</strong> {TRADESHOW_ACCOUNT_MANAGER_NAME}
              </div>
              <AutoNote>
                {t('tradeshow.autoNote', { email: EMAIL_ADDRESSES.tradeshowAccountManager })}
              </AutoNote>
            </AccountBlock>

            <SubmitButton type="submit" disabled={submitting}>
              {submitting ? t('tradeshow.submitting') : t('tradeshow.submit')}
            </SubmitButton>
            <RecaptchaNotice>
              <Trans
                i18nKey="tradeshow.recaptchaNotice"
                components={{
                  privacy: <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" />,
                  terms: <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" />,
                }}
              />
            </RecaptchaNotice>
          </Form>
        )}

        <Slogan>
          {t('tradeshow.sloganLine1')}
          <br />
          {t('tradeshow.sloganLine2')}
        </Slogan>
      </Sheet>
    </Page>
  );
};
